/**
 * One `PATCH`, with the bytes counted as they leave. DG.21.
 *
 * ## Why this exists at all, when everything else here is `fetch`
 *
 * `fetch` cannot report upload progress. There is no event for it, and the alternative -
 * a `ReadableStream` request body - needs `duplex: 'half'`, which is not available in
 * every browser this has to run in and is exactly the audience that needs this most: a
 * prospect on a phone on cellular. `XMLHttpRequest` has `upload.onprogress` and has had it
 * for fifteen years.
 *
 * So the one request whose body is large enough to be worth watching uses XHR, and the
 * three that are not - create, `HEAD`, commit, poll - stay on `fetch`. That is a knowing
 * choice rather than a regression to the older API: the reason the newer one is used
 * everywhere else is that it is nicer, and the reason it is not used here is that it
 * cannot do the job.
 *
 * ## What it does not do
 *
 * It does not decide what progress *means*. `upload.onprogress` reports bytes handed to
 * the operating system, which is not the same as bytes the server has accepted and
 * committed to an offset - a connection can drop with 6MB "sent" and the server holding
 * none of it. Section 09 makes the server the authority on the offset and the whole tus
 * decision rests on distrusting a client's own count, so the caller treats what this
 * reports as *in flight* and reconciles to `Upload-Offset` when the chunk lands.
 *
 * ## The Response it fabricates
 *
 * The caller reads `ok`, `status`, `headers` and sometimes the body, so those are real.
 * `Response` is constructible in every browser that has `fetch`, and building one here
 * means `sendChunk` cannot tell which transport it got - which is the point, because the
 * offset handling and the retry logic must not fork.
 */

/**
 * Statuses the `Response` constructor refuses to pair with a body, per the fetch specification.
 *
 * The tus append answers **204**, so this is the common path rather than an edge: passing even an
 * empty string for one of these throws `TypeError: Response with null body status cannot have body`,
 * and the throw lands in the caller's catch - which reads it as a dropped connection and retries a
 * request that in fact succeeded. Found by the guest surface's own tests, which script a 204.
 */
const NULL_BODY = new Set([101, 103, 204, 205, 304]);

/** What a surface has to add to a bare XHR: its own credentials. */
export interface XhrCredentials {
  /** Sends cookies. The prospect surface's session is one; a representative's is a header. */
  readonly withCredentials?: boolean;
  /** Applied after the caller's own headers, so a surface cannot be talked out of its auth. */
  readonly headers?: Readonly<Record<string, string>>;
}

/**
 * Issue `init` at `url` over XHR, reporting bytes as they leave.
 *
 * Rejects the same way `fetch` does - only when the request never completed - so the
 * caller's existing offline handling is unchanged. A 4xx or 5xx resolves.
 */
export function xhrSend(
  url: string,
  init: RequestInit,
  onSent: (bytes: number) => void,
  credentials: XhrCredentials = {},
): Promise<Response> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(init.method ?? 'POST', url, true);
    xhr.responseType = 'arraybuffer';
    if (credentials.withCredentials === true) xhr.withCredentials = true;

    for (const [name, value] of new Headers(init.headers).entries()) {
      xhr.setRequestHeader(name, value);
    }
    for (const [name, value] of Object.entries(credentials.headers ?? {})) {
      xhr.setRequestHeader(name, value);
    }

    xhr.upload.addEventListener('progress', (event) => {
      // `lengthComputable` is false while the body is being framed. Reporting zero then
      // would make the bar jump backwards, which reads as the upload restarting.
      if (event.lengthComputable) onSent(event.loaded);
    });

    xhr.addEventListener('load', () => {
      resolve(
        new Response(NULL_BODY.has(xhr.status) ? null : (xhr.response as ArrayBuffer), {
          status: xhr.status,
          statusText: xhr.statusText,
          headers: parseHeaders(xhr.getAllResponseHeaders()),
        }),
      );
    });

    // All three mean the request never completed, which is what the caller maps to its
    // own offline error. A status of 0 would otherwise reach `Response`, which refuses it.
    xhr.addEventListener('error', () => reject(new Error('The connection dropped.')));
    xhr.addEventListener('timeout', () => reject(new Error('The connection timed out.')));
    xhr.addEventListener('abort', () => reject(new Error('The upload was stopped.')));

    // `body` is a `Blob` here - `file.slice(...)`, a view rather than a copy - which XHR
    // sends without reading it into memory. Anything else would defeat the point of
    // chunking a 350MB file in the first place.
    xhr.send(init.body as XMLHttpRequestBodyInit);
  });
}

/**
 * `getAllResponseHeaders` returns one CRLF-delimited string, per XHR's own specification.
 *
 * Not a general parser: header values may contain a colon, so only the first is a
 * separator, and `Upload-Offset` is the one this exists to carry correctly.
 */
function parseHeaders(raw: string): Headers {
  const headers = new Headers();
  for (const line of raw.trim().split(/[\r\n]+/)) {
    const at = line.indexOf(':');
    if (at <= 0) continue;
    headers.set(line.slice(0, at).trim(), line.slice(at + 1).trim());
  }
  return headers;
}
